import React from 'react';

import { NewCoreIconProps } from '@atlaskit/icon';
import AddIcon from '@atlaskit/icon/core/add';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import GoalIcon from '@atlaskit/icon/core/goal';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import ObjectiveIcon from '@atlaskit/icon/core/objective';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem } from '@townsquare/analytics-nav4';
import { useCreateGoal } from '@townsquare/create-goal-modal';
import { archivedTql, followingTql } from '@townsquare/directory-header/utils';
import { filterUndefined } from '@townsquare/filter-type';
import { SideNavItem } from '@townsquare/griffin-navigation/types';
import { SideNavProductItem } from '@townsquare/navigation-config/nav4';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import {
	goalDirectoryArchivedRoute,
	goalDirectoryFollowingRoute,
	goalDirectoryRoute,
} from '@townsquare/routes/goal-directory-view';
import { goalUpdateViewRoute, goalsAppUpdateViewRoute } from '@townsquare/routes/goal-updates-view';
import { goalRoute } from '@townsquare/routes/goal-view';
import { goalsAppSavedViewsUpdatesRoute } from '@townsquare/routes/saved-views-updates-view';
import { fg } from '@townsquare/stat-sig/gate';
import { useCurrentUser } from '@townsquare/user-store';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ExternalSideNavItem } from './ExternalSideNavItem';
import { messages } from './messages';

export const useSideNavItems = (): SideNavItem[] => {
	const intl = useIntl();
	const hasNoWorkspace = useNoWorkspaceExperience();
	const routeDependencies = useRouteDependencies();
	const { openCreateGoal } = useCreateGoal({ shouldOpenModal: true });

	const currentUser = useCurrentUser().accountId;
	const followingTqlQuery = encodeURI(followingTql(currentUser));
	const isAppUpdateFeedEnabled = fg('atlas_app_update_feeds_enabled');
	const isSuccessMeasuresEnabled = fg('townsquare_goals_success_measures_prod');

	return [
		{
			id: SideNavProductItem.GOALS,
			disableCustomisation: true,
			analyticsSubject: AnalyticsSideNavItem.GOALS,
			path: generatePath(goalDirectoryRoute, routeDependencies),
			matches: [goalDirectoryRoute, goalRoute],
			name: intl.formatMessage(messages.goalDirectoryNavLabel),
			testId: 'side-nav-goals',
			Icon: isSuccessMeasuresEnabled ? ObjectiveIcon : GoalIcon,
			hoverAction: hasNoWorkspace
				? undefined
				: {
						Icon: (iconProps: NewCoreIconProps) => <AddIcon color="currentColor" {...iconProps} size="small" />,
						label: intl.formatMessage(messages.createNewGoalButtonLabel),
						onClick: () => {
							openCreateGoal({
								redirectOnSave: true,
								triggeredFrom: 'townsquare-side-nav-home',
							});
						},
					},
		},
		{
			id: SideNavProductItem.GOALS_FOLLOWING,
			analyticsSubject: AnalyticsSideNavItem.GOALS_FOLLOWING,
			path: hasNoWorkspace
				? generatePath(goalDirectoryRoute, routeDependencies)
				: `${generatePath(goalDirectoryFollowingRoute, routeDependencies)}?tql=${followingTqlQuery}&viewTab=following`,
			matches: [goalDirectoryFollowingRoute],
			name: intl.formatMessage(messages.followingNavLabel),
			testId: 'side-nav-following',
			Icon: EyeOpenIcon,
		},
		{
			id: SideNavProductItem.UPDATES,
			analyticsSubject: AnalyticsSideNavItem.UPDATES,
			path: generatePath(isAppUpdateFeedEnabled ? goalsAppUpdateViewRoute : goalUpdateViewRoute, routeDependencies),
			matches: [isAppUpdateFeedEnabled ? goalsAppUpdateViewRoute : goalUpdateViewRoute, goalsAppSavedViewsUpdatesRoute],
			name: intl.formatMessage(messages.statusUpdatesNavLabel),
			testId: 'side-nav-status-updates',
			Icon: MegaphoneIcon,
			openInNewTab: !isAppUpdateFeedEnabled,
			...(!isAppUpdateFeedEnabled
				? {
						Component: ExternalSideNavItem,
						isDraggable: true,
					}
				: {}),
		},
		{
			id: SideNavProductItem.GOALS_ARCHIVED,
			analyticsSubject: AnalyticsSideNavItem.GOALS_ARCHIVED,
			path: hasNoWorkspace
				? generatePath(goalDirectoryRoute, routeDependencies)
				: `${generatePath(goalDirectoryArchivedRoute, routeDependencies)}?tql=${archivedTql}&viewTab=archived`,
			matches: [goalDirectoryArchivedRoute],
			name: intl.formatMessage(messages.archivedNavLabel),
			testId: 'side-nav-archived',
			Icon: ArchiveBoxIcon,
		},
	].filter(filterUndefined);
};
